import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useNotification } from "@hooks/useNotification";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import {
  SvgBlackTrash,
  SvgChangeName,
  SvgFile,
  Caret,
  SvgEmployeeNotesEdit
} from "@routes/IconeSvg";
import { debounce } from "lodash";
import moment from "moment-timezone";

import FormTextarea from "@components/formGroup/formTextarea";
import SharedViewFile from "@components/modals/SharedViewFile";
import { Menu, Switch } from "@components/tailwindComponents";

import { IndividualObjective } from "@services/all.services";

import IndividualObjectiveScore from "./subComponents/IndividualObjectiveScore";
import ModalEditEmployeeNote from "./subComponents/ModalEditEmployeeNote";

/**
 * @type {typeof Tooltip}
 */
// @ts-ignore
const BlackTooltip = withStyles(() => ({
  tooltip: {
    maxWidth: "350px",
    backgroundColor: "#3F3F51",
    fontFamily: "Poppins_Medium",
    fontSize: "12px",
    padding: "8px 17px"
  }
}))(Tooltip);

/**
 *
 * @param {Object} props
 * @param {Object} props.individualObjective
 * @param {() => void} props.onOpenCloseMenu
 * @param {(individualObjective: any) => void} props.onUpdate
 * @param {(individualObjective: any) => void} props.onDelete
 * @param {boolean} props.readOnly
 * @param {boolean} props.isMyEmployeeSpace
 * @returns
 */
export default function IndividualObjectiveItem(props) {
  const { individualObjective, onOpenCloseMenu, isMyEmployeeSpace } = props;
  const { t: translate, i18n } = useTranslation("assesment.frequency");
  const [open, setOpen] = useState(false);
  const [isEmployeeNoteModalOpen, setIsEmployeeNoteModalOpen] = useState(false);
  const [sharedViewFile, setSharedViewFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [managerNote, setManagerNote] = useState(
    individualObjective.managerNote || ""
  );
  const [employeeNote, setEmployeeNote] = useState(
    individualObjective.employeeNote || ""
  );

  const formatedLastUpdate = useMemo(() => {
    return moment(individualObjective.lastUpdate)
      .locale(i18n.language)
      .format("LL");
  }, [individualObjective.lastUpdate, i18n.language]);

  const [employeeVisibility, setEmployeeVisibility] = useState(null);

  useEffect(() => {
    if (employeeVisibility === null) {
      setEmployeeVisibility(individualObjective.employeeVisibility);
    }
  }, [individualObjective.employeeVisibility]);

  useEffect(() => {
    setManagerNote(individualObjective.managerNote || "");
    setEmployeeNote(individualObjective.employeeNote || "");
  }, [
    individualObjective._id,
    individualObjective.managerNote,
    individualObjective.employeeNote
  ]);

  const { showError, showSuccess } = useNotification();
  const updateObjectiveVisibility = useCallback(
    debounce((nextValue) => {
      IndividualObjective.updateById({
        _id: individualObjective._id,
        employeeVisibility: nextValue
      })
        .then(() => {
          showSuccess("update_action_success", { object: "objective" });
        })
        .catch((error) => {
          console.error(error);
          setEmployeeVisibility((prevstate) => !prevstate);
          showError("error");
        });
    }, 1000),
    [individualObjective, showError, showSuccess]
  );

  // const updateManagerNote = useCallback(
  //   debounce((nextValue) => {
  //     IndividualObjective.updateById({
  //       _id: individualObjective._id,
  //       managerNote: nextValue,
  //     }).catch((error) => {
  //       console.error(error);
  //       setManagerNote(individualObjective.managerNote || "");
  //       showError("error");
  //     });
  //   }, 1000),
  //   [individualObjective, showError],
  // );

  // const updateEmployeeNote = useCallback(
  //   debounce((nextValue) => {
  //     IndividualObjective.updateById({
  //       _id: individualObjective._id,
  //       employeeNote: nextValue,
  //     }).catch((error) => {
  //       console.error(error);
  //       setEmployeeNote(individualObjective.employeeNote || "");
  //       showError("error");
  //     });
  //   }, 1000),
  //   [individualObjective, showError],
  // );

  const handleToggleOpen = () => {
    setOpen(!open);
  };

  function handleVisibilityChange() {
    setEmployeeVisibility((prevstate) => {
      updateObjectiveVisibility(!prevstate);
      return !prevstate;
    });
  }

  // Conserver ces handlers si l'édition inline doit être réactivée plus tard.
  // function handleManagerNoteChange(event) {
  //   const nextValue = event.target.value;
  //   setManagerNote(nextValue);
  //   updateManagerNote(nextValue);
  // }

  // function handleEmployeeNoteChange(event) {
  //   const nextValue = event.target.value;
  //   setEmployeeNote(nextValue);
  //   updateEmployeeNote(nextValue);
  // }

  function handleOpenEmployeeNoteModal(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsEmployeeNoteModalOpen(true);
  }

  function handleCloseEmployeeNoteModal() {
    setIsEmployeeNoteModalOpen(false);
  }

  function handleEmployeeNoteModalSuccess(nextEmployeeNote) {
    setEmployeeNote(nextEmployeeNote);
  }

  function onClickShowFile(file) {
    setSelectedFile(file);
    setSharedViewFile((prevstate) => !prevstate);
  }

  const objectiveFiles = useMemo(() => {
    if (
      Array.isArray(individualObjective?.files) &&
      individualObjective.files.length
    ) {
      return individualObjective.files;
    }

    return individualObjective?.file ? [individualObjective.file] : [];
  }, [individualObjective]);

  const totalCols = useMemo(() => {
    let count = 6; // Désignation, Type, Commentaire, Fichier joint, Dernière modification, Note
    if (!props.readOnly && !isMyEmployeeSpace) count++; // Visibilité employé
    if (!props.readOnly) count++; // Actions menu
    return count;
  }, [props.readOnly, isMyEmployeeSpace]);

  return (
    <React.Fragment>
      <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable hover:arh-bg-greyColor">
        {/* Désignation avec caret intégré */}
        <td className="arh-px-2 arh-py-3">
          <div className="arh-flex arh-items-center arh-gap-2">
            <div
              className="arh-flex arh-shrink-0 arh-transform arh-cursor-pointer arh-transition arh-duration-[100ms] arh-ease-in"
              onClick={handleToggleOpen}
            >
              <Caret className={`${open ? "arh-rotate-90" : "arh-rotate-0"}`} />
            </div>
            <BlackTooltip
              placement="top"
              title={individualObjective.designation}
            >
              <div className="arh-line-clamp-2 arh-text-xs arh-font-medium arh-text-darkColor">
                {individualObjective.designation}
              </div>
            </BlackTooltip>
          </div>
        </td>

        {/* Type */}
        <td className="arh-px-2 arh-py-3">
          <div className="arh-text-xs arh-font-medium arh-text-darkColor">
            {individualObjective.objectiveType?.name || "-"}
          </div>
        </td>

        {/* Commentaire / Explication */}
        <td className="arh-px-2 arh-py-3">
          <BlackTooltip
            placement="top"
            title={individualObjective.comment}
          >
            <div className="arh-line-clamp-2 arh-text-xs arh-font-medium arh-text-darkColor">
              {individualObjective.comment}
            </div>
          </BlackTooltip>
        </td>

        {/* Fichier joint */}
        <td className="arh-px-2 arh-py-3">
          {objectiveFiles?.length > 0
            ? objectiveFiles.map((file) => (
                <BlackTooltip
                  key={file?._id}
                  placement="top"
                  title={file?.filename || file?.name || "-"}
                >
                  <div
                    className="arh-flex arh-cursor-pointer arh-items-center arh-gap-1"
                    onClick={() => onClickShowFile(file)}
                  >
                    <span className="arh-shrink-0">
                      <SvgFile />
                    </span>
                    <span className="arh-line-clamp-1 arh-text-xs arh-font-medium arh-text-darkColor arh-underline">
                      {file?.filename || file?.name || "-"}
                    </span>
                  </div>
                </BlackTooltip>
              ))
            : "-"}
        </td>

        {/* Visibilité employé */}
        {!props.readOnly && !isMyEmployeeSpace && (
          <td className="arh-px-2 arh-py-3">
            <div className="arh-flex arh-items-center arh-gap-3">
              <label className="!arh-mb-0 arh-text-xs">
                {translate("Oui")}
              </label>
              <span className="arh-inline-flex arh-px-1">
                <Switch
                  color="arh-bg-greenColor"
                  checked={employeeVisibility}
                  handleChange={handleVisibilityChange}
                  rotate={true}
                />
              </span>
              <label className="!arh-mb-0 arh-text-xs">
                {translate("Non")}
              </label>
            </div>
          </td>
        )}

        {/* Dernière modification */}
        <td className="arh-px-2 arh-py-3 arh-text-xs arh-font-medium arh-text-darkColor">
          {formatedLastUpdate}
        </td>

        {/* Note / Score */}
        <td className="arh-px-2 arh-py-3">
          <IndividualObjectiveScore
            individualObjective={individualObjective}
            readOnly={props.readOnly}
          />
        </td>

        {/* Actions */}
        {!props.readOnly && !isMyEmployeeSpace && (
          <td className="arh-px-2 arh-py-3">
            <div className="position_relative">
              <Menu
                lists={[
                  {
                    icon: <SvgChangeName />,
                    title: "Modifier",
                    click: () => props.onUpdate(individualObjective)
                  },
                  {
                    icon: <SvgBlackTrash />,
                    title: "Effacer",
                    click: () => props.onDelete(individualObjective)
                  }
                ]}
                withIcon
                onOpenCloseMenu={onOpenCloseMenu}
              />
            </div>
          </td>
        )}
      </tr>

      {open && (
        <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable">
          <td
            colSpan={totalCols}
            className="arh-bg-greyColor arh-px-2  arh-py-3"
          >
            <div className="arh-grid arh-grid-cols-2 arh-gap-4">
              <div className="arh-relative  arh-ml-[0.5rem] arh-w-[96%]">
                <FormTextarea
                  label={translate("Notes gestionnaire")}
                  value={managerNote}
                  // onChange={handleManagerNoteChange}
                  readOnly
                  rows={4}
                  ParentForm="arh-mb-0"
                  classLabel="!arh-text-black"
                  classTextarea="arh-h-[96px] !arh-px-4 !arh-py-3 !arh-bg-white !arh-text-black !arh-border-greyBorderInput"
                />
              </div>
              <div className="arh-relative arh-w-[96%]">
                {isMyEmployeeSpace && (
                  <button
                    type="button"
                    className="arh-absolute arh-right-3 arh-top-[34px] arh-z-10 arh-cursor-pointer arh-border-0 arh-bg-transparent arh-p-0"
                    onClick={handleOpenEmployeeNoteModal}
                  >
                    <SvgEmployeeNotesEdit />
                  </button>
                )}
                <FormTextarea
                  label={translate("Notes employé")}
                  value={employeeNote}
                  // onChange={handleEmployeeNoteChange}
                  readOnly
                  rows={4}
                  ParentForm="arh-mb-0"
                  classLabel="!arh-text-black"
                  classTextarea={`arh-h-[96px] !arh-px-4 !arh-py-3 !arh-bg-white !arh-text-black !arh-border-greyBorderInput ${isMyEmployeeSpace ? "!arh-pr-12" : ""}`}
                />
              </div>
            </div>
          </td>
        </tr>
      )}

      <SharedViewFile
        onClickClose={onClickShowFile}
        show={sharedViewFile}
        file={selectedFile}
        t={translate}
      />

      <ModalEditEmployeeNote
        isOpen={isEmployeeNoteModalOpen}
        onCloseModal={handleCloseEmployeeNoteModal}
        initialEmployeeNote={employeeNote}
        individualObjectiveId={individualObjective._id}
        onSuccess={handleEmployeeNoteModalSuccess}
      />
    </React.Fragment>
  );
}
